/**
 * One labelled section of the Playbook Live briefing: a small muted label
 * (`<dt>`) over body text (`<dd>`), grouped in a `<div>` as a direct child
 * of the briefing's `<dl>`.
 *
 * Long paragraphs (the meeting premise) clamp to a few lines behind a
 * "Show more" toggle so the Sales Playbook panel below keeps most of the
 * column. jsdom cannot measure overflow, so "long" is a character count.
 */
import React, { useId, useState } from 'react';

/** Text longer than this gets clamped; about three lines at the panel's width. */
export const BRIEFING_CLAMP_THRESHOLD_CHARS = 180;

const COPY = {
  showMore: 'Show more',
  showLess: 'Show less',
} as const;

interface BriefingSectionProps {
  label: string;
  /** Span both columns of the briefing grid (long paragraphs). */
  isFullWidth?: boolean;
  testId?: string;
  /** Extra grid placement classes (e.g. spanning two rows). */
  layoutClassName?: string;
  /** Plain text to clamp when long; ignored when `children` is given. */
  text?: string;
  children?: React.ReactNode;
}

export const BriefingSection: React.FC<BriefingSectionProps> = ({
  label,
  isFullWidth = false,
  testId,
  layoutClassName,
  text,
  children,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const bodyId = useId();
  const isClampable = !children && (text?.length ?? 0) > BRIEFING_CLAMP_THRESHOLD_CHARS;

  return (
    <div
      className={[isFullWidth ? 'col-span-2 min-w-0' : 'min-w-0', layoutClassName].filter(Boolean).join(' ')}
      data-testid={testId}
    >
      <dt className="text-xs font-semibold uppercase tracking-wide text-ink-muted">{label}</dt>
      <dd className="mt-0.5 text-sm leading-body-s text-ink-secondary break-words">
        <div
          id={bodyId}
          data-testid={testId ? `${testId}-body` : undefined}
          className={isClampable && !isExpanded ? 'line-clamp-3' : undefined}
        >
          {children ?? text}
        </div>
        {isClampable && (
          <button
            type="button"
            className="mt-0.5 block text-xs font-medium text-signature hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-signature rounded-control"
            aria-expanded={isExpanded}
            aria-controls={bodyId}
            onClick={() => setIsExpanded((wasExpanded) => !wasExpanded)}
          >
            {isExpanded ? COPY.showLess : COPY.showMore}
          </button>
        )}
      </dd>
    </div>
  );
};

export default BriefingSection;
